import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
} from "firebase/firestore";
import {
    Package,
    Plus,
    Edit,
    Trash2,
    Search,
    X,
    Save,
    Loader,
    DollarSign,
    Tag,
    Image as ImageIcon,
    Store,
    AlertCircle,
    TrendingUp,
    BarChart3,
} from "lucide-react";

const ProductManagerEnhanced = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [stores, setStores] = useState([]);
    const [storeInventories, setStoreInventories] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        category: "",
        basePrice: "",
        image: "",
        sku: "",
        isActive: true,
    });
    const [saving, setSaving] = useState(false);

    const categories = [
        "PAQUETES",
        "ENTRADAS",
        "PARA ACOMPAÑAR",
        "BEBIDAS",
        "POSTRES",
        "OTROS",
    ];

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        try {
            // Cargar productos
            const productsSnap = await getDocs(collection(db, "products"));
            const productsData = productsSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setProducts(productsData);

            // Cargar sucursales
            const storesSnap = await getDocs(collection(db, "stores"));
            const storesData = storesSnap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setStores(storesData);

            // Cargar inventarios de todas las sucursales
            const inventoriesSnap = await getDocs(collection(db, "storeInventory"));
            const inventoriesData = {};
            inventoriesSnap.docs.forEach((doc) => {
                inventoriesData[doc.id] = doc.data();
            });
            setStoreInventories(inventoriesData);
        } catch (error) {
            console.error("Error fetching data:", error);
            alert("Error al cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    const getProductStats = (productId, basePrice) => {
        const stats = {
            storesWithProduct: 0,
            minPrice: basePrice,
            maxPrice: basePrice,
            avgPrice: basePrice,
            totalStock: 0,
            priceVariations: [],
        };

        Object.entries(storeInventories).forEach(([storeId, inventory]) => {
            const productData = inventory.products?.[productId];
            if (productData) {
                stats.storesWithProduct++;
                stats.totalStock += productData.stock || 0;

                const customPrice = productData.customPrice || basePrice;
                stats.priceVariations.push({
                    storeId,
                    storeName: inventory.storeName,
                    price: customPrice,
                    stock: productData.stock || 0,
                });

                stats.minPrice = Math.min(stats.minPrice, customPrice);
                stats.maxPrice = Math.max(stats.maxPrice, customPrice);
            }
        });

        if (stats.priceVariations.length > 0) {
            stats.avgPrice = stats.priceVariations.reduce((sum, item) => sum + item.price, 0) / stats.priceVariations.length;
        }

        return stats;
    };

    const handleOpenModal = (product = null) => {
        if (product) {
            setEditingProduct(product);
            setFormData({
                name: product.name || "",
                description: product.description || "",
                category: product.category || "",
                basePrice: product.basePrice?.toString() || "",
                image: product.image || "",
                sku: product.sku || "",
                isActive: product.isActive !== false,
            });
        } else {
            setEditingProduct(null);
            setFormData({
                name: "",
                description: "",
                category: "",
                basePrice: "",
                image: "",
                sku: "",
                isActive: true,
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingProduct(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const generateSKU = () => {
        const randomStr = Math.random().toString(36).substring(2, 8).toUpperCase();
        return `PRD-${randomStr}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert("El nombre del producto es obligatorio");
            return;
        }
        if (!formData.category) {
            alert("La categoría es obligatoria");
            return;
        }
        if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
            alert("El precio base debe ser mayor a 0");
            return;
        }

        setSaving(true);
        try {
            const productData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                category: formData.category,
                basePrice: parseFloat(formData.basePrice),
                image: formData.image.trim(),
                sku: formData.sku.trim() || generateSKU(),
                isActive: formData.isActive,
                updatedAt: serverTimestamp(),
            };

            if (editingProduct) {
                await updateDoc(doc(db, "products", editingProduct.id), productData);
                alert("Producto actualizado exitosamente");
            } else {
                productData.createdAt = serverTimestamp();
                await addDoc(collection(db, "products"), productData);
                alert("Producto creado exitosamente");
            }

            handleCloseModal();
            fetchAllData();
        } catch (error) {
            console.error("Error saving product:", error);
            alert("Error al guardar el producto");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (productId, productName) => {
        if (
            window.confirm(
                `¿Estás seguro de que deseas eliminar el producto "${productName}"?\n\nEsto NO eliminará el stock de las sucursales, pero el producto ya no estará disponible para agregar a nuevas sucursales.`
            )
        ) {
            try {
                await deleteDoc(doc(db, "products", productId));
                alert("Producto eliminado exitosamente");
                fetchAllData();
            } catch (error) {
                console.error("Error deleting product:", error);
                alert("Error al eliminar el producto");
            }
        }
    };

    const filteredProducts = products.filter((product) =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const productsByCategory = filteredProducts.reduce((acc, product) => {
        const category = product.category || "SIN CATEGORÍA";
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(product);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Loader className="w-12 h-12 text-[#FF0000] animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Cargando productos...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Catálogo de Productos</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {products.length} producto{products.length !== 1 ? "s" : ""} · {stores.length} sucursal{stores.length !== 1 ? "es" : ""}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate("/admin?tab=stock")}
                        className="bg-blue-50 text-blue-600 px-4 py-2.5 rounded-xl font-semibold hover:bg-blue-100 transition-all flex items-center gap-2"
                    >
                        <BarChart3 className="w-5 h-5" strokeWidth={2} />
                        Ver Inventario
                    </button>
                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-[#FF0000] text-white px-4 py-2.5 rounded-xl font-semibold hover:bg-[#CC0000] transition-all flex items-center gap-2 shadow-md"
                    >
                        <Plus className="w-5 h-5" strokeWidth={2} />
                        Nuevo Producto
                    </button>
                </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-gray-400" strokeWidth={2} />
                    <input
                        type="text"
                        placeholder="Buscar por nombre, categoría o SKU..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 bg-transparent text-gray-800 text-sm font-medium placeholder-gray-400 focus:outline-none"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery("")}
                            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 text-gray-600" />
                        </button>
                    )}
                </div>
            </div>

            {/* Products by Category */}
            {Object.keys(productsByCategory).length > 0 ? (
                <div className="space-y-6">
                    {Object.entries(productsByCategory).map(([category, categoryProducts]) => (
                        <div key={category}>
                            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#FF0000]">
                                {category} ({categoryProducts.length})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {categoryProducts.map((product) => {
                                    const stats = getProductStats(product.id, product.basePrice);
                                    const hasPriceVariation = stats.minPrice !== stats.maxPrice;

                                    return (
                                        <div
                                            key={product.id}
                                            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all"
                                        >
                                            {/* Product Image */}
                                            {product.image ? (
                                                <div className="relative">
                                                    <img
                                                        src={product.image}
                                                        alt={product.name}
                                                        className="w-full h-40 object-cover"
                                                    />
                                                    {hasPriceVariation && (
                                                        <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1">
                                                            <TrendingUp className="w-3 h-3" strokeWidth={2.5} />
                                                            Precios Variados
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                                                    <ImageIcon className="w-12 h-12 text-gray-400" strokeWidth={1.5} />
                                                    {hasPriceVariation && (
                                                        <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1">
                                                            <TrendingUp className="w-3 h-3" strokeWidth={2.5} />
                                                            Variado
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Product Content */}
                                            <div className="p-4">
                                                <div className="flex items-start justify-between mb-2">
                                                    <h4 className="text-base font-bold text-gray-900 line-clamp-1 flex-1">
                                                        {product.name}
                                                    </h4>
                                                    <span
                                                        className={`px-2 py-1 rounded-full text-xs font-semibold ml-2 ${product.isActive
                                                                ? "bg-green-100 text-green-700"
                                                                : "bg-red-100 text-red-700"
                                                            }`}
                                                    >
                                                        {product.isActive ? "Activo" : "Inactivo"}
                                                    </span>
                                                </div>

                                                {product.description && (
                                                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                                                        {product.description}
                                                    </p>
                                                )}

                                                {/* Pricing Info */}
                                                <div className="space-y-2 mb-4">
                                                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                                        <span className="text-xs text-gray-600 font-semibold">
                                                            Precio Base:
                                                        </span>
                                                        <span className="text-lg font-bold text-[#FF0000]">
                                                            ${product.basePrice}
                                                        </span>
                                                    </div>

                                                    {hasPriceVariation && (
                                                        <div className="p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                                                            <div className="flex items-center justify-between text-xs mb-1">
                                                                <span className="text-gray-600">Rango:</span>
                                                                <span className="font-bold text-gray-900">
                                                                    ${stats.minPrice.toFixed(2)} - ${stats.maxPrice.toFixed(2)}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-gray-600">Promedio:</span>
                                                                <span className="font-semibold text-gray-900">
                                                                    ${stats.avgPrice.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Store Stats */}
                                                    <div className="flex items-center justify-between text-xs">
                                                        <div className="flex items-center gap-1 text-gray-600">
                                                            <Store className="w-3 h-3" strokeWidth={2} />
                                                            <span>
                                                                {stats.storesWithProduct} de {stores.length} sucursales
                                                            </span>
                                                        </div>
                                                        <span className="text-gray-500">
                                                            Stock: {stats.totalStock}
                                                        </span>
                                                    </div>

                                                    {product.sku && (
                                                        <div className="flex items-center gap-2">
                                                            <Tag className="w-4 h-4 text-gray-400" strokeWidth={2} />
                                                            <span className="text-xs text-gray-500 font-mono">
                                                                {product.sku}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleOpenModal(product)}
                                                        className="flex-1 bg-blue-50 text-blue-600 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                                                    >
                                                        <Edit className="w-4 h-4" strokeWidth={2} />
                                                        Editar
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(product.id, product.name)}
                                                        className="bg-red-50 text-red-600 px-3 py-2 rounded-lg font-semibold text-sm hover:bg-red-100 transition-colors flex items-center justify-center"
                                                    >
                                                        <Trash2 className="w-4 h-4" strokeWidth={2} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" strokeWidth={1.5} />
                    <p className="text-gray-500">
                        {searchQuery
                            ? "No se encontraron productos"
                            : "No hay productos registrados"}
                    </p>
                </div>
            )}

            {/* Modal (unchanged) */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">
                                {editingProduct ? "Editar Producto" : "Nuevo Producto"}
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Nombre del Producto *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                    placeholder="Ej: Pollo Entero"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                    placeholder="Descripción del producto"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Categoría *
                                    </label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                        required
                                    >
                                        <option value="">Seleccionar...</option>
                                        {categories.map((cat) => (
                                            <option key={cat} value={cat}>
                                                {cat}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Precio Base (MXN) *
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="basePrice"
                                        value={formData.basePrice}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                        placeholder="0.00"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    SKU
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        name="sku"
                                        value={formData.sku}
                                        onChange={handleInputChange}
                                        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                        placeholder="Generado automáticamente"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, sku: generateSKU() }))}
                                        className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                                    >
                                        Generar
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    URL de Imagen
                                </label>
                                <input
                                    type="text"
                                    name="image"
                                    value={formData.image}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent"
                                    placeholder="https://..."
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                    className="w-5 h-5 text-[#FF0000] rounded focus:ring-[#FF0000]"
                                />
                                <label className="text-sm font-semibold text-gray-700">
                                    Producto Activo
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                                    disabled={saving}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-[#FF0000] text-white px-4 py-2.5 rounded-xl font-semibold hover:bg-[#CC0000] transition-colors flex items-center justify-center gap-2"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <Loader className="w-5 h-5 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" strokeWidth={2} />
                                            {editingProduct ? "Actualizar" : "Crear"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductManagerEnhanced;


